// src/components/SalarioCalculator.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { Form, Button, Container, Row, Col, Card, Alert } from 'react-bootstrap';
import '../styles/calculator.css'; // Importa tus estilos personalizados
import BotonMenu from './BotonMenu';
import Banners from './Banners';

const SalarioCalculator = () => {
    const [salarioBruto, setSalarioBruto] = useState('');
    const [tiempoTrabajado, setTiempoTrabajado] = useState('');
    const [resultados, setResultados] = useState(null);
    const [error, setError] = useState('');
    const navigate = useNavigate(); 

    const calcularDescuentos = () => {
        setError('');
        setResultados(null);

        const salario = parseFloat(salarioBruto);

        if (isNaN(salario) || salario <= 0) {
            setError('Por favor, ingresa un salario mensual bruto válido y positivo.');
            return;
        }

        // --- Cálculos de Ley en El Salvador ---
        // Topes máximos y porcentajes (aproximados, pueden variar ligeramente con el tiempo)
        const TOPE_ISSS = 1000.00; // Salario máximo para cálculo de ISSS
        const TOPE_AFP_ISSS_AFILIADO = 710.00; // Salario máximo para cálculo de AFP y ISSS para afiliados

        // Porcentajes del trabajador
        const PORCENTAJE_ISSS_TRABAJADOR = 0.03; // 3%
        const PORCENTAJE_AFP_TRABAJADOR = 0.0725; // 7.25%

        // Porcentajes del empleador (solo informativos, no se descuentan al trabajador)
        const PORCENTAJE_ISSS_PATRONAL = 0.0775; // 7.75%
        const PORCENTAJE_AFP_PATRONAL = 0.875; // 8.75%

        // 1. Cálculo de ISSS (Trabajador)
        let baseISSS = Math.min(salario, TOPE_ISSS);
        const isssTrabajador = baseISSS * PORCENTAJE_ISSS_TRABAJADOR;

        // 2. Cálculo de AFP (Trabajador)
        let baseAFP = Math.min(salario, TOPE_AFP_ISSS_AFILIADO);
        const afpTrabajador = baseAFP * PORCENTAJE_AFP_TRABAJADOR;

        // 3. Cálculo de Renta (ISR)
        // Tablas de Renta simplificadas para ejemplo (deben ser las actuales del país)
        let renta = 0;
        if (salario > 472.00) { // Primer tramo gravado
            if (salario <= 895.24) {
                renta = (salario - 472.00) * 0.10 + 17.67;
            } else if (salario <= 2038.10) {
                renta = (salario - 895.24) * 0.20 + 60.00;
            } else { // Más de 2038.10
                renta = (salario - 2038.10) * 0.30 + 288.57;
            }
        }
        // Nota: Las tablas de renta en El Salvador son más complejas, con tramos y cuotas fijas.
        // Este es un ejemplo simplificado. Además, ISSS y AFP son gastos deducibles para renta.
        // Un cálculo exacto necesitaría deducir ISSS y AFP antes de aplicar la tabla.
        const salarioGravadoRenta = salario - isssTrabajador - afpTrabajador;
        // La lógica de renta debe ser aplicada al salario gravado
        // A continuación, la lógica de renta más precisa, ajustando la base:
        renta = 0; // Resetear para el cálculo ajustado
        if (salarioGravadoRenta > 472.00) {
            if (salarioGravadoRenta <= 895.24) {
                renta = (salarioGravadoRenta - 472.00) * 0.10 + 17.67;
            } else if (salarioGravadoRenta <= 2038.10) {
                renta = (salarioGravadoRenta - 895.24) * 0.20 + 60.00;
            } else {
                renta = (salarioGravadoRenta - 2038.10) * 0.30 + 288.57;
            }
        }
        if (renta < 0) renta = 0; // Asegurarse que la renta no sea negativa

        // Total Descuentos del Trabajador
        const totalDescuentosMensual = isssTrabajador + afpTrabajador + renta;
        const salarioLiquidoMensual = salario - totalDescuentosMensual;

        // --- Cálculo Quincenal ---
        const salarioQuincenal = salario / 2;
        const isssTrabajadorQuincenal = isssTrabajador / 2;
        const afpTrabajadorQuincenal = afpTrabajador / 2;
        const rentaQuincenal = renta / 2; // La renta se calcula mensual y se divide para pago quincenal
        const totalDescuentosQuincenal = isssTrabajadorQuincenal + afpTrabajadorQuincenal + rentaQuincenal;
        const salarioLiquidoQuincenal = salarioQuincenal - totalDescuentosQuincenal;

        setResultados({
            salarioBruto: salario,
            isssTrabajador: isssTrabajador.toFixed(2),
            afpTrabajador: afpTrabajador.toFixed(2),
            renta: renta.toFixed(2),
            totalDescuentosMensual: totalDescuentosMensual.toFixed(2),
            salarioLiquidoMensual: salarioLiquidoMensual.toFixed(2),
            salarioQuincenal: salarioQuincenal.toFixed(2),
            isssTrabajadorQuincenal: isssTrabajadorQuincenal.toFixed(2),
            afpTrabajadorQuincenal: afpTrabajadorQuincenal.toFixed(2),
            rentaQuincenal: rentaQuincenal.toFixed(2),
            totalDescuentosQuincenal: totalDescuentosQuincenal.toFixed(2),
            salarioLiquidoQuincenal: salarioLiquidoQuincenal.toFixed(2),
            tiempoTrabajado: tiempoTrabajado // Mantiene el valor seleccionado para mostrarlo
        });
    };

    const resetForm = () => {
        setSalarioBruto('');
        setTiempoTrabajado('');
        setResultados(null);
        setError('');
    };

    return (
        <Container className="my-5">
            <Row className="justify-content-md-center">
                <Banners BannerShow={1} Act={1}></Banners>
                <Col md={8} lg={8}>
                <BotonMenu></BotonMenu>
                    <Card className="card-custom p-4" style={{ marginBottom: '20px' }}>
                        <Card.Title className="text-center mb-4">
                            <h2>Calculadora de salarios y descuentos de ley de El Salvador</h2>
                        </Card.Title>
                        {error && <Alert variant="danger">{error}</Alert>}
                        <Form>
                            <Form.Group className="mb-3" controlId="formSalarioBruto">
                                <Form.Label>Salario Mensual Bruto ($)</Form.Label>
                                <Form.Control
                                    type="number"
                                    placeholder="Ej: 500.00"
                                    value={salarioBruto}
                                    onChange={(e) => setSalarioBruto(e.target.value)}
                                    step="0.01"
                                    min="0"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="formTiempoTrabajado">
                                <Form.Label>Tiempo Trabajado en la Empresa</Form.Label>
                                <Form.Select
                                    value={tiempoTrabajado}
                                    onChange={(e) => setTiempoTrabajado(e.target.value)}
                                >
                                    <option value="">Selecciona una opción</option>
                                    <option value="1a3">1 a 3 años</option>
                                    <option value="3a10">3 a 10 años</option>
                                    <option value="10mas">10 o más años</option>
                                </Form.Select>
                            </Form.Group>

                            <div className="d-grid gap-2">
                                <Button variant="primary" className="btn-primary-custom" onClick={calcularDescuentos}>
                                    CALCULAR DESCUENTOS
                                </Button>
                                <Button variant="secondary" onClick={resetForm}>
                                    Limpiar
                                </Button>
                            </div>
                        </Form>

                        {resultados && (
                            <div className="result-section mt-4">
                                <h4 className="text-center mb-3">Resumen de Cálculo</h4>
                                <div className="row">
                                    <Col md={6}>
                                        <h5 className="text-center span">Cálculo Mensual</h5>
                                        <div className="result-item">
                                            <span>Salario Bruto:</span> <span className="float-end value">${resultados.salarioBruto.toFixed(2)}</span>
                                        </div>
                                        <div className="result-item">
                                            <span>Descuento ISSS:</span> <span className="float-end value">${resultados.isssTrabajador}</span>
                                        </div>
                                        <div className="result-item">
                                            <span>Descuento AFP:</span> <span className="float-end value">${resultados.afpTrabajador}</span>
                                        </div>
                                        <div className="result-item">
                                            <span>Descuento Renta:</span> <span className="float-end value">${resultados.renta}</span>
                                        </div>
                                        <div className="result-item border-top pt-2 mt-2">
                                            <strong>Total Descuentos:</strong> <span className="float-end value">${resultados.totalDescuentosMensual}</span>
                                        </div>
                                        <div className="result-item border-top border-bottom py-2 my-2 total-liquid-salary">
                                            <strong>Salario Líquido:</strong> <span className="float-end value">${resultados.salarioLiquidoMensual}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <h5 className="text-center span">Cálculo Quincenal</h5>
                                        <div className="result-item">
                                            <span>Salario Bruto Quincenal:</span> <span className="float-end value">${resultados.salarioQuincenal}</span>
                                        </div>
                                        <div className="result-item">
                                            <span>Descuento ISSS:</span> <span className="float-end value">${resultados.isssTrabajadorQuincenal}</span>
                                        </div>
                                        <div className="result-item">
                                            <span>Descuento AFP:</span> <span className="float-end value">${resultados.afpTrabajadorQuincenal}</span>
                                        </div>
                                        <div className="result-item">
                                            <span>Descuento Renta:</span> <span className="float-end value">${resultados.rentaQuincenal}</span>
                                        </div>
                                        <div className="result-item border-top pt-2 mt-2">
                                            <strong>Total Descuentos:</strong> <span className="float-end value">${resultados.totalDescuentosQuincenal}</span>
                                        </div>
                                        <div className="result-item border-top border-bottom py-2 my-2 total-liquid-salary">
                                            <strong>Salario Líquido:</strong> <span className="float-end value">${resultados.salarioLiquidoQuincenal}</span>
                                        </div>
                                    </Col>
                                </div>
                                <hr />
                                <p className="mb-0 text-muted small text-center">
                                    Este cálculo es una estimación basada en las leyes de El Salvador. Los topes y tramos pueden variar con el tiempo. Consulta a un experto o la normativa vigente para un cálculo exacto.
                                </p>
                            </div>
                            
                        )}
                       
                    </Card>
                    <Row>
                        <Banners BannerShow={2} Act={1}></Banners>
                    </Row>                    
                </Col>
                <Banners BannerShow={1} Act={1}></Banners>
            </Row>
        </Container>
    );
};

export default SalarioCalculator;